import { registerFigure } from "./base.js";
/**
 * Two or three waves side by side, travelling.
 *
 * The other kind where movement is the content. Pitch, colour, radio, the sea:
 * a child asked to believe that a squiggle with more bumps sounds higher has
 * been given a fact, not a picture. Waves that move at the same speed while one
 * fits more waves into the same space show why without a sentence.
 *
 * Compares rather than annotates. A single labelled wave with "crest" and
 * "trough" pointing at it is a textbook diagram — useful to a teacher, and to a
 * seven-year-old just a word next to a bump.
 */
export const waveFigure = registerFigure({
    kind: "wave",
    summary: "Two or three travelling waves, compared by how tightly packed they are.",
    chooseWhen: "The answer is that one thing is higher, bluer, or more energetic than " +
        "another because its waves are closer together — a big drum against a " +
        "small one, red light against blue, a low voice against a high one. Each " +
        "value is how many waves fit in the same space, so a bigger number means a " +
        "tighter, higher-pitched wave. Relative numbers are fine here; they say " +
        "which is which, not how many hertz.",
    issues(figure) {
        const labels = figure.labels ?? [];
        const values = figure.values ?? [];
        if (labels.length < 2)
            return ["A wave picture compares at least two waves."];
        if (labels.length > 3)
            return ["More than 3 waves stops being a comparison."];
        if (values.length !== labels.length) {
            return [`${values.length} numbers for ${labels.length} waves — there must be one each.`];
        }
        // A wave with no waves in it is a flat line, and a negative count is not a
        // thing the renderer can draw.
        if (values.some((v) => v <= 0))
            return ["Every wave count must be greater than zero."];
        // Identical waves drawn side by side make a comparison that shows nothing.
        if (new Set(values).size === 1)
            return ["The waves must differ, or there is nothing to see."];
        if (labels.some((l) => l.trim().length === 0))
            return ["A wave needs a name."];
        return [];
    },
    normalise(figure) {
        const labels = (figure.labels ?? []).map((l) => l.trim());
        return {
            kind: "wave",
            caption: figure.caption,
            labels,
            values: (figure.values ?? []).slice(0, labels.length),
        };
    },
    example: {
        kind: "wave",
        caption: "Tighter waves reach your ear as a higher note.",
        labels: ["Big drum", "Small drum"],
        values: [2, 6],
    },
});
//# sourceMappingURL=wave.js.map